import { AxiosResponse } from 'axios';
import { ApiService } from 'common/services';
import { ScopesAssignmentsBody, UpdateUserInfoBody, UserFilter, UserUpdateBody } from 'common/types/common';
import { resolveApiBase } from 'common/utils/resolve-api-base';
import { detectAppType } from 'common/utils/app-type';
import { ChangePasswordBody, CreateAssignmentBody, GetMeResponse, UsersResponse } from './users-api.types';

const handleResolvePath = (path: string) => {
  const appType = detectAppType();

  if (appType === 'supermodel') {
    return `/supermodel${path}`;
  }

  return path;
};

class UsersApiService extends ApiService {
  getUsers = (args?: UserFilter): Promise<AxiosResponse<UsersResponse>> => {
    return this.get('/users', {
      params: {
        ...(args?.page ? { offset: args.page * args.limit } : {}),
        ...(args?.limit ? { limit: args?.limit } : {}),
        ...(args?.search ? { search: args?.search } : {}),
        ...(args?.vendorsIDs?.length ? { vendorsIDs: args?.vendorsIDs } : {}),
        ...(args?.scopeIDs?.length ? { scopeIDs: args?.scopeIDs } : {}),
        ...(args?.roleID ? { roleID: args?.roleID } : {}),
        ...(args?.excludeRoleID ? { excludeRoleID: args?.excludeRoleID } : {}),
      },
    });
  };

  createAssignment = (body: CreateAssignmentBody) => {
    return this.post('/users/assignments', body);
  };

  getMe = (): Promise<AxiosResponse<GetMeResponse>> => {
    return this.get(handleResolvePath('/users/me'));
  };

  updateUser = (id: string, body: UserUpdateBody): Promise<AxiosResponse<{ message: string }>> => {
    return this.patch(`/users/${id}`, body);
  };

  deleteUser = (id: string): Promise<AxiosResponse<{ message: string }>> => {
    return this.delete(`/users/${id}`);
  };

  changePassword = (body: ChangePasswordBody) => {
    return this.post(handleResolvePath('/users/change-password'), body);
  };

  postScopesAssignments = (body: ScopesAssignmentsBody) => {
    return this.post('/users/vendors/assignments', body);
  };

  deleteScopesAssignments = (body: ScopesAssignmentsBody) => {
    return this.delete('/users/vendors/scopes/assignments', body);
  };

  updateUserInfo = (id: string, body: UpdateUserInfoBody): Promise<AxiosResponse<{ message: string }>> => {
    return this.patch(handleResolvePath(`/users/${id}/info`), body);
  };
}

export const usersApi = new UsersApiService(resolveApiBase());
